import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import api from './services/api';
import NoticeBoard from './pages/NoticeBoard';
import LedgerView from './pages/LedgerView';
import SupportTickets from './pages/SupportTickets';
import FacilityBooking from './pages/FacilityBooking';
import Polls from './pages/Polls';
import Procurement from './pages/Procurement';
import GroupBuys from './pages/GroupBuys';
import DocumentVault from './pages/DocumentVault';
import PLStatements from './pages/PLStatements';
import Profile from './pages/Profile';
import Login from './pages/Login';
import ChatWidget from './components/ChatWidget';
import {
  Bell,
  Calendar,
  CreditCard,
  FileText,
  HelpCircle,
  Menu,
  Package,
  PieChart,
  User,
  X,
  ChevronDown,
  LogOut,
  Vault,
  BarChart3,
  UserCircle
} from 'lucide-react';
import { cn } from './lib/utils';

const ProtectedRoute = ({ children }: any) => {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" />;
  return children;
};

const navItems = [
  { name: 'Notices', path: '/', icon: Bell },
  { name: 'Resident Handbook', path: '/vault', icon: Vault },
  { name: 'Bookings', path: '/booking', icon: Calendar },
  { name: 'Ledger', path: '/finance', icon: CreditCard },
  { name: 'P&L', path: '/pl-statements', icon: BarChart3 },
  { name: 'Polls', path: '/polls', icon: PieChart },
  { name: 'Community', path: '/community', icon: Package },
  { name: 'Tenders', path: '/procurement', icon: FileText },
  { name: 'Support', path: '/tickets', icon: HelpCircle },
];

const Sidebar = ({ isOpen, setIsOpen }: { isOpen: boolean, setIsOpen: (o: boolean) => void }) => {
  const location = useLocation();
  
  return (
    <aside className={cn(
      "fixed inset-y-0 left-0 z-50 w-64 bg-white text-slate-900 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-auto border-r border-slate-200",
      isOpen ? "translate-x-0" : "-translate-x-full"
    )}>
      <div className="h-full flex flex-col p-4">
        <div className="flex items-center gap-3 mb-8 px-2 mt-2">
          <div className="w-8 h-8 bg-violet-600 rounded-lg flex items-center justify-center text-white font-bold">M</div>
          <span className="font-bold text-slate-900 tracking-tight leading-none text-lg">Society Mitra</span>
          <button onClick={() => setIsOpen(false)} className="lg:hidden ml-auto p-2 hover:bg-slate-100 rounded-xl text-slate-500">
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto space-y-1 scrollbar-hide">
          {navItems.map((item) => {
            const isActive = item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 text-sm font-medium",
                  isActive
                    ? "bg-violet-50 text-violet-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                )}
              >
                <item.icon size={18} className={cn(isActive ? "text-violet-600" : "text-slate-500")} />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </div>
    </aside>
  );
};

function App() {
  const [profile, setProfile] = useState<any>(null);
  const [units, setUnits] = useState<any[]>([]);
  const [activeUnitId, setActiveUnitId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const pRes = await api.get('/profiles/me');
        setProfile(pRes.data);
        const uRes = await api.get('/profiles/me/units');
        setUnits(uRes.data);
        if (uRes.data.length > 0) {
          const savedUnit = localStorage.getItem('activeUnitId');
          if (savedUnit && uRes.data.find((u: any) => u.id === savedUnit)) {
            setActiveUnitId(savedUnit);
          } else {
            setActiveUnitId(uRes.data[0].id);
            localStorage.setItem('activeUnitId', uRes.data[0].id);
          }
        }
      } catch (err) {
        console.error(err);
        localStorage.removeItem('token');
        window.location.href = '/login';
      } finally {
        setLoading(false);
      }
    };
    if (localStorage.getItem('token')) init();
    else setLoading(false);
  }, []);

  const handleUnitChange = (id: string) => {
    setActiveUnitId(id);
    localStorage.setItem('activeUnitId', id);
  };

  const activeUnit = units.find((u: any) => u.id === activeUnitId);

  if (loading) return <div className="h-screen flex items-center justify-center text-violet-600 font-medium">Society Mitra...</div>;

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={
          <ProtectedRoute>
            <div className="min-h-screen bg-[#FAFAF9] lg:flex font-sans text-slate-900 overflow-hidden">
              <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

              <div className="flex-1 flex flex-col h-screen overflow-hidden relative">
                <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between z-40 sticky top-0">
                  <div className="flex items-center gap-4">
                    <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-xl">
                      <Menu size={20} />
                    </button>
                  </div>

                  <div className="flex items-center gap-4 ml-auto">
                    <div className="relative group hidden sm:block">
                       <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 transition-colors">
                          <span className="w-2 h-2 rounded-full bg-emerald-500" />
                          {activeUnit ? `Unit ${activeUnit.unit_number}` : 'Select Unit'}
                          <ChevronDown size={14} className="text-slate-400" />
                       </button>
                       <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all py-1 z-50">
                          {units.map((u: any) => (
                            <button
                              key={u.id}
                              onClick={() => handleUnitChange(u.id)}
                              className={cn(
                                "w-full text-left px-4 py-2 text-sm hover:bg-slate-50",
                                activeUnitId === u.id ? "text-violet-600 font-bold bg-violet-50/50" : "text-slate-600"
                              )}
                            >
                              Unit {u.unit_number} {u.block ? `(${u.block})` : ''}
                            </button>
                          ))}
                       </div>
                    </div>

                    <Link to="/profile" className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:text-violet-600 transition-colors">
                      <UserCircle size={20} />
                    </Link>
                    <button
                      onClick={() => { localStorage.removeItem('token'); window.location.href = '/login'; }}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <LogOut size={20} />
                    </button>
                  </div>
                </header>

                <main className="flex-1 overflow-y-auto p-4 sm:p-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
                  <div className="container mx-auto max-w-5xl">
                    <Routes>
                      <Route path="/" element={<NoticeBoard />} />
                      <Route path="/booking" element={<FacilityBooking activeUnitId={activeUnitId} />} />
                      <Route path="/finance" element={<LedgerView activeUnitId={activeUnitId} />} />
                      <Route path="/pl-statements" element={<PLStatements />} />
                      <Route path="/polls" element={<Polls />} />
                      <Route path="/community" element={<GroupBuys />} />
                      <Route path="/procurement" element={<Procurement />} />
                      <Route path="/vault" element={<DocumentVault />} />
                      <Route path="/tickets" element={<SupportTickets activeUnitId={activeUnitId} />} />
                      <Route path="/profile" element={<Profile />} />
                    </Routes>
                  </div>
                </main>
              </div>

              <ChatWidget />
            </div>
          </ProtectedRoute>
        } />
      </Routes>
    </Router>
  );
}

export default App;
